import { useCallback, useEffect, useRef, useState } from "react";
import {
  buildLoyaltyProgramFormData,
  fetchLoyaltyProgram,
  saveLoyaltyProgram,
  updateLoyaltyProgram,
} from "../../../apiService/loyalty.service";
import { MAX_EVENT_CHUNKS, STATUS } from "../constants";
import { createEventChunk, getApiErrorMessage, mapApiEventToChunk, toInputValue } from "../utils";

export const useProgramForm = ({ programId, selectedDomain, onSaved }) => {
  const isEdit = Boolean(programId);
  const [titleEn, setTitleEn] = useState("");
  const [titleHi, setTitleHi] = useState("");
  const [descriptionEn, setDescriptionEn] = useState("");
  const [descriptionHi, setDescriptionHi] = useState("");
  const [status, setStatus] = useState(STATUS.ACTIVE);
  const [image, setImage] = useState(null);
  const [existingImage, setExistingImage] = useState("");
  const [eventChunks, setEventChunks] = useState([createEventChunk()]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(isEdit);
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);

  useEffect(() => {
    if (!programId) return undefined;

    let cancelled = false;

    const loadProgram = async () => {
      setIsLoading(true);
      setError("");
      try {
        const program = await fetchLoyaltyProgram(programId);
        if (cancelled) return;
        if (!program) {
          setError("Loyalty program not found.");
          return;
        }
        setTitleEn(toInputValue(program.title_en));
        setTitleHi(toInputValue(program.title_hi));
        setDescriptionEn(toInputValue(program.description_en));
        setDescriptionHi(toInputValue(program.description_hi));
        setStatus(program.status === STATUS.INACTIVE ? STATUS.INACTIVE : STATUS.ACTIVE);
        setExistingImage(program.image || "");
        const events = Array.isArray(program.loyalty_events) ? program.loyalty_events : [];
        setEventChunks(events.length ? events.map(mapApiEventToChunk) : [createEventChunk()]);
      } catch (loadError) {
        if (!cancelled) {
          setError(getApiErrorMessage(loadError, "Failed to load loyalty program."));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadProgram();
    return () => {
      cancelled = true;
    };
  }, [programId]);

  const updateEventChunk = useCallback((chunkId, field, value) => {
    setEventChunks((prev) =>
      prev.map((chunk) => {
        if (chunk.id !== chunkId) return chunk;
        if (field === "is_streak" && value === false) {
          return { ...chunk, is_streak: false, streak_duration: "", streak_coins: "" };
        }
        return { ...chunk, [field]: value };
      })
    );
  }, []);

  const addEventChunk = useCallback(() => {
    setEventChunks((prev) =>
      prev.length >= MAX_EVENT_CHUNKS ? prev : [...prev, createEventChunk()]
    );
  }, []);

  const removeEventChunk = useCallback((chunkId) => {
    setEventChunks((prev) => (prev.length === 1 ? prev : prev.filter((chunk) => chunk.id !== chunkId)));
  }, []);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    if (savingRef.current) return;
    const title_en = titleEn.trim();
    if (!title_en) {
      setError("English title is required.");
      return;
    }
    if (!selectedDomain?.id) {
      setError("Select a product in the header before saving.");
      return;
    }

    const formData = buildLoyaltyProgramFormData({
      id: programId,
      productId: selectedDomain.id,
      titleEn: title_en,
      titleHi: titleHi.trim(),
      descriptionEn: descriptionEn.trim(),
      descriptionHi: descriptionHi.trim(),
      status,
      image,
      events: eventChunks,
    });

    setError("");
    savingRef.current = true;
    setIsSaving(true);
    try {
      if (isEdit) {
        await updateLoyaltyProgram(formData);
      } else {
        await saveLoyaltyProgram(formData);
      }
      onSaved();
    } catch (saveError) {
      if (saveError.code === "ERR_NETWORK" || saveError.message === "Network Error") {
        setError("Cannot reach the loyalty API. Please try again.");
      } else {
        setError(getApiErrorMessage(saveError, "Failed to save loyalty program."));
      }
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  }, [
    descriptionEn,
    descriptionHi,
    eventChunks,
    image,
    isEdit,
    onSaved,
    programId,
    selectedDomain,
    status,
    titleEn,
    titleHi,
  ]);

  return {
    isEdit,
    isLoading,
    isSaving,
    error,
    titleEn,
    setTitleEn,
    titleHi,
    setTitleHi,
    descriptionEn,
    setDescriptionEn,
    descriptionHi,
    setDescriptionHi,
    status,
    setStatus,
    setImage,
    existingImage,
    eventChunks,
    updateEventChunk,
    addEventChunk,
    removeEventChunk,
    handleSubmit,
  };
};
